import { textBox } from '../paint/text';
import { clampRect } from '../selection';
import { rotatedBounds } from './bounds';
import { compositeBounds, groupComposites, sceneSkeleton } from './composite';
import { activeMask } from './mask';
import { isGroup, removeLeaves, topLevelAncestorId } from './tree';
import type {
  BlendMode,
  EditorDocument,
  FillLayer,
  ImageLayer,
  Layer,
  PaintLayer,
  Rect,
  Size,
  TextLayer,
} from './types';

/**
 * Merging layers: the geometry half.
 *
 * Same split as `core/transforms.ts` — work out *what* to rasterise here, in
 * pure code that can be unit-tested, and let `io/raster.ts` do the drawing.
 *
 * Image, paint, fill and text layers can be merged — text is rasterised with
 * the same renderer the export path uses, exactly like every editor's "merge
 * down rasterizes text". Shape and adjustment layers have no pixels (shape
 * tools bake to paint rasters; adjustments are direct pixel edits) and are
 * refused with a clear reason rather than silently dropped.
 */

export type RasterizableLayer = ImageLayer | PaintLayer | FillLayer;

/** A layer flattened into everything needed to draw it into a region. */
export interface LayerSource {
  id: string;
  kind: 'image' | 'paint' | 'fill' | 'text';
  /** Data URL for image/paint layers. */
  src?: string;
  /** CSS colour for fill layers, which cover the whole canvas. */
  color?: string;
  /** The layer itself for text sources — the renderer needs the full style. */
  text?: TextLayer;
  x: number;
  y: number;
  /** Unrotated drawn size; rotation is applied on top of this. */
  width: number;
  height: number;
  /** Degrees, clockwise about the layer's drawn centre — matching the engine. */
  angle: number;
  opacity: number;
  blendMode: BlendMode;
  /** The mask in force, if any — already resolved, so `enabled` is not carried. */
  mask?: { src: string; inverted: boolean };
}

export interface MergeDownPlan {
  label: string;
  /** Name for the merged layer: the lower layer keeps its identity. */
  name: string;
  region: Rect;
  targetId: string;
  targetIndex: number;
  removedId: string;
  /** Bottom first, then the layer merging into it. */
  sources: LayerSource[];
}

export interface FlattenPlan {
  label: string;
  name: string;
  region: Rect;
  background: string;
  sources: RasterSource[];
}

/**
 * A source the rasteriser can draw (Iteration 14).
 *
 * A plain {@link LayerSource} is one leaf. A `composite` renders its children
 * into their own buffer first and places the result with one opacity and one
 * blend mode — the group as a compositor citizen. A `clip` renders its base,
 * then stencils each clipped layer by the base's rendered alpha; the result is
 * placed with the *base's* blend mode, which is why the base travels with it.
 *
 * This type lives in core because the *plans* build it — `io/raster.ts` only
 * executes what it is given, and core never imports io.
 */
export type RasterSource =
  | LayerSource
  | {
      kind: 'composite';
      opacity: number;
      blendMode: BlendMode;
      region: Rect;
      sources: RasterSource[];
    }
  | { kind: 'clip'; region: Rect; base: RasterSource; clipped: RasterSource[] };

export function isRasterizable(layer: Layer): layer is RasterizableLayer {
  return layer.type === 'image' || layer.type === 'paint' || layer.type === 'fill';
}

/** A layer whose pixels can be produced for a merge: raster types plus text. */
export function isMergeable(layer: Layer): boolean {
  return isRasterizable(layer) || layer.type === 'text';
}

export function layerSource(layer: Layer, doc: Size): LayerSource | null {
  if (layer.type === 'text') {
    const box = textBox(layer);
    return {
      id: layer.id,
      kind: 'text',
      text: layer,
      x: layer.x,
      y: layer.y,
      width: box.width * layer.scaleX,
      height: box.height * layer.scaleY,
      angle: layer.angle,
      opacity: layer.opacity,
      blendMode: layer.blendMode,
    };
  }
  if (!isRasterizable(layer)) return null;
  const mask = activeMask(layer);
  if (layer.type === 'fill') {
    return {
      id: layer.id,
      kind: 'fill',
      color: layer.color,
      x: 0,
      y: 0,
      width: doc.width,
      height: doc.height,
      angle: 0,
      opacity: layer.opacity,
      blendMode: layer.blendMode,
    };
  }
  // Unrotated drawn size: rotation is applied on top of this, and
  // `sourceRegion` works out where that leaves the pixels. Image and paint
  // layers carry their raster size in the same fields, scale included.
  const width = layer.naturalWidth * layer.scaleX;
  const height = layer.naturalHeight * layer.scaleY;
  return {
    id: layer.id,
    kind: layer.type,
    src: layer.type === 'image' ? layer.src : layer.pixels,
    x: layer.x,
    y: layer.y,
    width,
    height,
    angle: layer.angle,
    opacity: layer.opacity,
    blendMode: layer.blendMode,
    ...(mask ? { mask: { src: mask.src, inverted: mask.inverted } } : {}),
  };
}

/** Where a source actually lands once rotation is applied. */
export function sourceRegion(source: LayerSource): Rect {
  return rotatedBounds(
    { x: source.x, y: source.y, width: source.width, height: source.height },
    source.angle,
  );
}

function unionOf(sources: LayerSource[], doc: Size): Rect {
  if (sources.length === 0) return { x: 0, y: 0, width: doc.width, height: doc.height };
  const regions = sources.map(sourceRegion);
  const left = Math.min(...regions.map((region) => region.x));
  const top = Math.min(...regions.map((region) => region.y));
  const right = Math.max(...regions.map((region) => region.x + region.width));
  const bottom = Math.max(...regions.map((region) => region.y + region.height));
  return clampRect(
    { x: left, y: top, width: right - left, height: bottom - top },
    {
      x: 0,
      y: 0,
      width: doc.width,
      height: doc.height,
    },
  );
}

/** Why a merge is impossible, so the UI can say so instead of doing nothing. */
export type MergeDownRejection =
  | 'not-found'
  | 'bottom-layer'
  | 'locked'
  | 'unsupported'
  | 'composite';

export interface MergeDownResult {
  plan: MergeDownPlan | null;
  reason?: MergeDownRejection;
}

/**
 * Plan a merge of the layer at `index` with the one below it.
 *
 * Merging happens between **siblings**: two layers in the same list. A layer
 * inside a group merges with the layer below it *inside that group*, and a
 * group is never merged into — it has no pixels of its own, so there is
 * nothing to bake.
 *
 * `parent` is the group holding this sibling list, when there is one: since
 * Iteration 14 a merge inside a composite group would bake a half-composited
 * context into someone else's pixels, and a merge involving a clipped layer
 * would lose its stencil, so both refuse with `composite`.
 */
export function planMergeDown(
  doc: Size & { width: number; height: number },
  siblings: Layer[],
  index: number,
  parent: Layer | null = null,
): MergeDownResult {
  if (index < 0 || index >= siblings.length) return { plan: null, reason: 'not-found' };
  if (index === 0) return { plan: null, reason: 'bottom-layer' };

  const top = siblings[index];
  const bottom = siblings[index - 1];
  if (!top || !bottom) return { plan: null, reason: 'not-found' };
  if (bottom.locked) return { plan: null, reason: 'locked' };
  if (parent && groupComposites(parent)) return { plan: null, reason: 'composite' };
  if (top.clipping || bottom.clipping) return { plan: null, reason: 'composite' };
  if (!isMergeable(top) || !isMergeable(bottom)) {
    return { plan: null, reason: 'unsupported' };
  }

  const bottomSource = layerSource(bottom, doc);
  const topSource = layerSource(top, doc);
  if (!bottomSource || !topSource) return { plan: null, reason: 'unsupported' };

  return {
    plan: {
      label: 'Merge Down',
      name: bottom.name,
      region: unionOf([bottomSource, topSource], doc),
      targetId: bottom.id,
      targetIndex: index - 1,
      removedId: top.id,
      sources: [bottomSource, topSource],
    },
  };
}

/** Flatten every visible layer into a single image layer. */
export function planFlatten(
  doc: Size & { layers: Layer[]; background: string },
): FlattenPlan | null {
  // The sources mirror the composite structure, not just the leaf list: a
  // non-neutral group contributes its buffer, a clipping run its stencilled
  // sheet, a folder its leaves — exactly what the engine draws (Iteration 14).
  const sources = flattenSources(doc.layers, doc);
  if (sources.length === 0) return null;

  return {
    label: 'Merge Visible',
    name: 'Background',
    region: { x: 0, y: 0, width: doc.width, height: doc.height },
    background: doc.background,
    sources,
  };
}

/**
 * One member of a clip run as a raster source: a mergeable leaf, a composite
 * group's buffer, or a folder flattened into a plain buffer (opacity 1,
 * `normal` — visually identical to drawing the leaves in place).
 */
function memberSource(layer: Layer, doc: Size): RasterSource | null {
  if (isGroup(layer)) {
    const children = flattenSources(layer.children, doc);
    if (children.length === 0) return null;
    const region = compositeBounds(layer.children, doc);
    if (!region) return null;
    return {
      kind: 'composite',
      opacity: layer.opacity,
      blendMode: layer.blendMode,
      region,
      sources: children,
    };
  }
  return layerSource(layer, doc);
}

/**
 * The raster sources for one sibling list, following the scene skeleton.
 *
 * A folder group's children are flattened into the list inline — they blend
 * against the scene below the group, which a buffer would isolate. Only a
 * *composite* group becomes a buffer, and only mergeable leaves contribute:
 * shape and adjustment layers have no pixels, exactly as before.
 */
function flattenSources(siblings: Layer[], doc: Size): RasterSource[] {
  const out: RasterSource[] = [];
  for (const unit of sceneSkeleton(siblings, doc)) {
    switch (unit.kind) {
      case 'leaf': {
        if (!unit.layer.visible) break;
        const source = layerSource(unit.layer, doc);
        if (source) out.push(source);
        break;
      }
      case 'folder':
        if (!unit.group.visible) break;
        out.push(...flattenSources(unit.group.children, doc));
        break;
      case 'buffer': {
        if (!unit.group.visible) break;
        const children = flattenSources(unit.group.children, doc);
        if (children.length === 0) break;
        out.push({
          kind: 'composite',
          opacity: unit.group.opacity,
          blendMode: unit.group.blendMode,
          region: unit.bounds,
          sources: children,
        });
        break;
      }
      case 'clip': {
        if (!unit.base.visible) break;
        const base = memberSource(unit.base, doc);
        if (!base) break;
        // A folder as a clipped member is expanded: its children take the
        // stencil individually, in z-order — that is what clipping a folder
        // means. A composite group stays one member: its buffer is what gets
        // stencilled.
        const clipped: RasterSource[] = [];
        for (const member of unit.clipped) {
          if (!member.visible) continue;
          if (isGroup(member) && !groupComposites(member)) {
            for (const child of member.children) {
              if (!child.visible) continue;
              const source = memberSource(child, doc);
              if (source) clipped.push(source);
            }
            continue;
          }
          const source = memberSource(member, doc);
          if (source) clipped.push(source);
        }
        if (clipped.length === 0) {
          out.push(base);
          break;
        }
        out.push({ kind: 'clip', region: unit.bounds, base, clipped });
        break;
      }
    }
  }
  return out;
}

/**
 * Every leaf id under a source tree, z-order preserved — the list a flatten
 * commit needs to know which layers it replaced.
 */
export function rasterSourceIds(sources: RasterSource[]): string[] {
  const ids: string[] = [];
  const walk = (list: RasterSource[]) => {
    for (const source of list) {
      if (source.kind === 'composite') walk(source.sources);
      else if (source.kind === 'clip') {
        walk([source.base]);
        walk(source.clipped);
      } else ids.push(source.id);
    }
  };
  walk(sources);
  return ids;
}

/**
 * The layer stack after a Merge Visible commits.
 *
 * The merge takes the visible layers; everything else — hidden layers above,
 * between or below them, and the groups that hold them — keeps its slot. The
 * merged layer lands where the topmost merged layer sat, which is where the
 * user's eye already is.
 *
 * `mergedIds` is in z-order (bottom first), because the topmost one decides
 * where the result lands.
 */
export function layerStackAfterFlatten(
  doc: EditorDocument,
  merged: ImageLayer,
  mergedIds: readonly string[],
): Layer[] {
  const removed = new Set(mergedIds);
  const layers = removeLeaves(doc.layers, removed);

  // The anchor is the top-level slot of the topmost merged layer: a layer
  // inside a group belongs to the group, and the group to the document.
  const topmost = mergedIds[mergedIds.length - 1];
  const anchorId = topLevelAncestorId(doc.layers, topmost ?? '') ?? topmost ?? '';
  let insertAt = 0;
  for (const original of doc.layers) {
    if (original.id === anchorId) break;
    if (layers.some((candidate) => candidate.id === original.id)) insertAt += 1;
  }
  const at = Math.max(0, Math.min(insertAt, layers.length));
  layers.splice(at, 0, merged);
  return layers;
}
